import { travelTimeMinutes } from '../utils/travelTimeOptions';
import React, { useState, useEffect } from 'react';
import {
  Button,
  FormControl,
  CircularProgress,
  Box,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Stop } from '../types';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect, {
  DropdownSelectOption
} from '../../../components/DropdownSelect/DropdownSelect';
import { User } from '../../../types/userType';
import { SnackbarSeverityType } from '../../../enums';
import { addStop } from '../../../apis/transport/stops';
import { isEmpty } from 'lodash';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import pallet from '../../../layouts/pallet';

interface AddStopDialogProps {
  open: boolean;
  onClose: () => void;
  onStopAdded: () => void;
  stops: Stop[];
  loggedInUser: User;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  selectedRouteId: string;
}

const AddStopDialog: React.FC<AddStopDialogProps> = ({
  open,
  onClose,
  onStopAdded,
  stops,
  loggedInUser,
  showToast,
  selectedRouteId
}) => {
  const [name, setName] = useState('');
  const [travelTime, setTravelTime] = useState('');
  const [mapsPin, setMapsPin] = useState('');
  const [offsetPin, setOffsetPin] = useState('');
  const [isFirstStop, setIsFirstStop] = useState(false);
  const [previousStopId, setPreviousStopId] = useState<string | null>(null);
  const [triggerStopId, setTriggerStopId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [stopOptions, setStopOptions] = useState<DropdownSelectOption[]>([]);
  const [triggerStopOptions, setTriggerStopOptions] = useState<
    DropdownSelectOption[]
  >([]);

  const travelTimeOptions: DropdownSelectOption[] = travelTimeMinutes.map(
    (minute) => ({
      value: `${minute}`,
      label: `${minute} min`
    })
  );

  useEffect(() => {
    if (open) {
      setName('');
      setTravelTime('');
      setMapsPin('');
      setOffsetPin('');
      if (stops.length === 0) {
        setIsFirstStop(true);
        setPreviousStopId(null);
      } else {
        setIsFirstStop(false);
        setPreviousStopId(stops[stops.length - 1].id);
      }
      setTriggerStopId(null);
      setIsLoading(false);
    }
  }, [open, stops]);

  useEffect(() => {
    if (stops) {
      setStopOptions(
        stops.map((stop) => ({ value: stop.id, label: stop.name }))
      );
    }
  }, [stops]);

  useEffect(() => {
    if (isFirstStop) {
      setTriggerStopOptions([]);
      setPreviousStopId(null);
      setTravelTime('0');
    } else {
      const addAfterStop = stops.find(
        (stop) => stop.id.toString() === previousStopId?.toString()
      );
      if (addAfterStop) {
        const filteredStops = stops.filter(
          (stop) => stop.stopNumber <= addAfterStop.stopNumber
        );
        const filteredOptions = filteredStops.map((stop) => ({
          value: stop.id,
          label: stop.name
        }));
        setTriggerStopOptions(filteredOptions);
      } else {
        setTriggerStopOptions([]);
      }
    }
    setTriggerStopId(null);
  }, [previousStopId, isFirstStop, stops]);

  const handleAddStop = async () => {
    setIsLoading(true);
    try {
      await addStop(selectedRouteId, {
        name,
        travelTime: travelTime,
        location: !isEmpty(mapsPin)
          ? {
              lat: mapsPin.split(',')[0]?.trim(),
              long: mapsPin.split(',')[1]?.trim()
            }
          : undefined,
        offsetLocation: !isEmpty(offsetPin)
          ? {
              lat: offsetPin.split(',')[0]?.trim(),
              long: offsetPin.split(',')[1]?.trim()
            }
          : undefined,
        addStopAfter: !isEmpty(previousStopId)
          ? (previousStopId as unknown as string)
          : undefined,
        triggerStop: !isEmpty(triggerStopId)
          ? (triggerStopId as unknown as string)
          : undefined,
        isFirstStop: isFirstStop === true ? true : undefined
      });
      showToast('Stop added successfully!', SnackbarSeverityType.success);
      onStopAdded();
      onClose();
    } catch (error) {
      showToast('Failed to add stop', SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const isAddButtonDisabled =
    !name ||
    !travelTime ||
    !mapsPin ||
    (!isFirstStop && !previousStopId) ||
    (!isFirstStop && !triggerStopId) ||
    isLoading;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Add a new Stop'
      actions={
        <>
          <Button onClick={onClose} color='primary' variant='outlined'>
            Cancel
          </Button>
          <Button
            onClick={handleAddStop}
            disabled={isAddButtonDisabled}
            variant='contained'
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Submit'
            )}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={isFirstStop}
                onChange={(e) => setIsFirstStop(e.target.checked)}
                sx={{
                  color: pallet.black,
                  '&.Mui-checked': {
                    color: pallet.black
                  }
                }}
                disabled={stops.length === 0}
              />
            }
            label='Is First Stop'
          />
        </FormControl>

        <DropdownSelect
          label={isFirstStop ? 'Add Stop After' : 'Add Stop After'}
          placeholder='Select Stop'
          name='previousStopId'
          value={previousStopId || ''}
          options={stopOptions}
          onChange={(e) => setPreviousStopId(e.target.value as string)}
          hasTopLabel
          disabled={isFirstStop}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextInputWithIcon
            label='Stop Name'
            placeholder='Enter Stop Name'
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            sx={{ flex: 1 }}
          />
          <DropdownSelect
            label={isFirstStop ? 'Travel Time' : 'Travel Time'}
            name='travelTime'
            placeholder='Select Travel Time'
            value={travelTime}
            options={travelTimeOptions}
            onChange={(e) => setTravelTime(e.target.value as string)}
            hasTopLabel
            required
            disabled={isFirstStop}
            sx={{ flex: 1 }}
          />
        </Box>
        <DropdownSelect
          label={isFirstStop ? 'Trigger to this Stop' : 'Trigger to this Stop'}
          name='triggerStopId'
          placeholder='Select Stop'
          value={triggerStopId || ''}
          options={triggerStopOptions}
          onChange={(e) => setTriggerStopId(e.target.value as string)}
          hasTopLabel
          required
          disabled={isFirstStop}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextInputWithIcon
            label='Google Maps Pin'
            required
            value={mapsPin}
            onChange={(e) => setMapsPin(e.target.value)}
            placeholder='19.1831, 72.8356'
            fullWidth
            sx={{ flex: 1 }}
          />
          <TextInputWithIcon
            label='Offset Location Pin'
            value={offsetPin}
            onChange={(e) => {
              setOffsetPin(e.target.value);
            }}
            placeholder='19.1831, 72.8356'
            fullWidth
            sx={{ flex: 1 }}
          />
        </Box>
      </Box>
    </DialogModal>
  );
};

export default AddStopDialog;
